"use client";

import { Show, SignInButton, UserButton } from "@clerk/nextjs";

import {
  BarChart3,
  Brain,
  Eye,
  FileSearch,
  LineChart,
  MessageSquare,
  Monitor,
  Moon,
  Network,
  ScanText,
  Shield,
  Table2,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import Link from "next/link";
import { Logo } from "@/components/Logo";
import { MainMenu } from "@/components/MainMenu";
import { ModeToggle } from "@/components/ModeToggle";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const CONTACT_URL = "https://calendar.app.google/4E1JwtdJMcbrdG1D8";

const DesktopHeroSection = () => (
  <section className="relative w-full px-4 py-28 mx-auto max-w-screen-xl text-center overflow-hidden">
    <div className="absolute inset-0 bg-grid-green/10 [mask-image:radial-gradient(white,transparent_70%)] pointer-events-none" />
    <div className="absolute inset-0 bg-geometric pointer-events-none" />

    <div className="absolute top-16 left-10 w-16 h-16 bg-green-500/5 rounded-lg flex items-center justify-center animate-float">
      <Monitor className="w-8 h-8 text-green-500/30" />
    </div>
    <div
      className="absolute top-24 right-14 w-14 h-14 bg-emerald-500/5 rounded-full flex items-center justify-center animate-float"
      style={{ animationDelay: "2s" }}
    >
      <LineChart className="w-7 h-7 text-emerald-500/30" />
    </div>
    <div
      className="absolute bottom-28 left-14 w-12 h-12 bg-teal-500/5 rounded-lg flex items-center justify-center animate-float"
      style={{ animationDelay: "4s" }}
    >
      <Brain className="w-6 h-6 text-teal-500/30" />
    </div>
    <div
      className="absolute bottom-16 right-18 w-14 h-14 bg-green-600/5 rounded-full flex items-center justify-center animate-float"
      style={{ animationDelay: "1s" }}
    >
      <Shield className="w-7 h-7 text-green-600/30" />
    </div>

    <div className="relative z-10">
      <p className="text-xs font-semibold tracking-[0.25em] uppercase text-muted-foreground/70 mb-6">
        Native macOS research desk
      </p>
      <h1 className="text-3xl font-bold leading-tight tracking-tighter md:text-5xl lg:text-6xl animate-gradient bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent">
        Find the signal before the market does.
      </h1>
      <p className="mt-6 text-lg text-muted-foreground md:text-xl max-w-3xl mx-auto">
        Turn filings, market data, and plain-English requests into
        source-grounded analysis, live monitors, and working financial tools —
        from one native desktop workspace. Analyze everything. Execute nothing.
      </p>
      <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
        <Button
          size="lg"
          className="text-lg px-8 py-4 shimmer card-hover gap-2"
          asChild
        >
          <a href={CONTACT_URL} target="_blank" rel="noreferrer">
            Request Desktop Access
          </a>
        </Button>
        <Button
          size="lg"
          variant="outline"
          className="text-lg px-8 py-4 card-hover"
          asChild
        >
          <Link href="/extract">See Kautious Extract</Link>
        </Button>
      </div>
      <p className="mt-6 text-sm text-muted-foreground">
        Hands-on onboarding for financial teams with a live workflow.
      </p>
    </div>
  </section>
);

const WhatYouGetSection = () => (
  <section className="relative py-24 bg-secondary/5">
    <div className="max-w-screen-xl mx-auto px-6">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">
          Replace research drag with decision speed.
        </h2>
        <div className="h-px w-16 mx-auto accent-line mb-6" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
        <Card className="p-8">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center mb-6">
            <MessageSquare className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-2xl font-semibold mb-4">Ask at full speed</h3>
          <p className="text-muted-foreground">
            Press{" "}
            <kbd className="px-2 py-0.5 rounded bg-muted text-xs font-mono">
              Cmd+Shift+K
            </kbd>{" "}
            and describe the answer or tool you need. Kautious generates sheets,
            monitors, reports, and research workflows from plain English — so
            the question does not die in a backlog.
          </p>
        </Card>

        <Card className="p-8">
          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center mb-6">
            <Eye className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-2xl font-semibold mb-4">Read-only by design</h3>
          <p className="text-muted-foreground">
            Connect your brokerage accounts and view holdings across Alpaca,
            Polygon, or CSV imports. Analyze everything. Execute nothing. Your
            team gets faster research without introducing trading risk.
          </p>
        </Card>

        <Card className="p-8">
          <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center mb-6">
            <FileSearch className="w-8 h-8 text-purple-600" />
          </div>
          <h3 className="text-2xl font-semibold mb-4">
            Turn filings into evidence
          </h3>
          <p className="text-muted-foreground">
            Search EDGAR, structure 10-Ks and 10-Qs through a five-tier parser
            stack, and turn XBRL into usable data while keeping the source trail
            visible.
          </p>
        </Card>

        <Card className="p-8">
          <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center mb-6">
            <TrendingUp className="w-8 h-8 text-orange-600" />
          </div>
          <h3 className="text-2xl font-semibold mb-4">
            Make forecasting compete
          </h3>
          <p className="text-muted-foreground">
            Put statistical, neural, ML, and foundation models head-to-head.
            Cross-validation and confidence intervals show which forecast
            deserves a place in the decision.
          </p>
        </Card>

        <Card className="p-8">
          <div className="w-16 h-16 bg-teal-100 dark:bg-teal-900/20 rounded-lg flex items-center justify-center mb-6">
            <Users className="w-8 h-8 text-teal-600" />
          </div>
          <h3 className="text-2xl font-semibold mb-4">
            Multiply analyst capacity
          </h3>
          <p className="text-muted-foreground">
            Analyst and Auditor agents handle repeatable work — earnings
            screening, thesis checks, and quality scoring — while your team
            applies judgment where it matters.
          </p>
        </Card>

        <Card className="p-8">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center mb-6">
            <ScanText className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-2xl font-semibold mb-4">Structure the mess</h3>
          <p className="text-muted-foreground">
            Turn earnings calls, filings, and articles into structured
            companies, metrics, dates, and relationships — ready for the next
            step in your workflow.
          </p>
        </Card>

        <Card className="p-8 md:col-span-2 md:max-w-xl md:mx-auto">
          <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/20 rounded-lg flex items-center justify-center mb-6">
            <Network className="w-8 h-8 text-indigo-600" />
          </div>
          <h3 className="text-2xl font-semibold mb-4">
            Keep your edge compounding
          </h3>
          <p className="text-muted-foreground">
            Kautious Graph tracks your research context, decisions, and
            reasoning across sessions. Each investigation becomes usable context
            for the next one instead of disappearing into another tab.
          </p>
        </Card>
      </div>
    </div>
  </section>
);

const BuiltForTheDeskSection = () => (
  <section className="relative py-24">
    <div className="max-w-screen-xl mx-auto px-6">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">
          Built for speed. Governed for a serious desk.
        </h2>
        <div className="h-px w-16 mx-auto accent-line mb-6" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
        <Card className="p-6 text-center hover:shadow-lg transition-shadow">
          <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center mx-auto mb-4">
            <Zap className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="font-semibold mb-2">Desktop-native</h3>
          <p className="text-sm text-muted-foreground">
            Tauri v2 app. Fast, lightweight, no Electron bloat.
          </p>
        </Card>

        <Card className="p-6 text-center hover:shadow-lg transition-shadow">
          <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center mx-auto mb-4">
            <Moon className="w-6 h-6 text-purple-600" />
          </div>
          <h3 className="font-semibold mb-2">Dark theme</h3>
          <p className="text-sm text-muted-foreground">
            Because you&apos;re staring at this all day.
          </p>
        </Card>

        <Card className="p-6 text-center hover:shadow-lg transition-shadow">
          <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center mx-auto mb-4">
            <Table2 className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="font-semibold mb-2">Spreadsheets that scale</h3>
          <p className="text-sm text-muted-foreground">
            AG Grid handles thousands of rows without breaking a sweat.
          </p>
        </Card>

        <Card className="p-6 text-center hover:shadow-lg transition-shadow">
          <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-6 h-6 text-orange-600" />
          </div>
          <h3 className="font-semibold mb-2">Real-time updates</h3>
          <p className="text-sm text-muted-foreground">
            WebSocket-connected to live market data.
          </p>
        </Card>

        <Card className="p-6 text-center hover:shadow-lg transition-shadow sm:col-span-2 lg:col-span-2 lg:max-w-xs lg:mx-auto">
          <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center mx-auto mb-4">
            <Shield className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="font-semibold mb-2">Your data stays yours</h3>
          <p className="text-sm text-muted-foreground">
            Local file I/O, credential backup on-device.
          </p>
        </Card>
      </div>
    </div>
  </section>
);

const HowItWorksSection = () => (
  <section className="relative py-24 bg-secondary/5">
    <div className="max-w-screen-xl mx-auto px-6">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">How it works</h2>
        <div className="h-px w-16 mx-auto accent-line mb-6" />
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          You bring the questions. Kautious routes them to the right engine.
        </p>
      </div>

      <div className="max-w-3xl mx-auto space-y-4">
        {[
          { num: 1, text: "Natural language prompt" },
          { num: 2, text: "AI classification" },
          { num: 3, text: "Preview generated" },
          { num: 4, text: "You approve or reject" },
          { num: 5, text: "Deployed to your workspace" },
        ].map((step) => (
          <div
            key={step.num}
            className="flex items-center gap-4 p-5 rounded-lg bg-background hover:bg-secondary/10 transition-colors border border-border/50"
          >
            <div className="flex-shrink-0 w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
              {step.num}
            </div>
            <p className="text-lg text-foreground">{step.text}</p>
          </div>
        ))}
      </div>

      <p className="text-center text-muted-foreground mt-10 text-sm font-medium">
        Nothing runs without your sign-off.
      </p>
    </div>
  </section>
);

const PositioningSection = () => (
  <section className="relative py-24">
    <div className="max-w-screen-xl mx-auto px-6 text-center">
      <h2 className="text-3xl md:text-4xl font-bold mb-6">
        A terminal shows data. Kautious turns it into work.
      </h2>
      <div className="h-px w-16 mx-auto accent-line mb-8" />
      <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
        Legacy research tools give every desk the same interface. Kautious gives
        your team source-grounded extraction, persistent context, custom
        workflows, and dynamic financial tools — without trade execution.
      </p>
    </div>
  </section>
);

const CTASection = () => (
  <section className="relative py-24 bg-gradient-to-r from-green-600 to-emerald-600 text-white">
    <div className="max-w-screen-xl mx-auto px-6 text-center">
      <h2 className="text-3xl md:text-4xl font-bold mb-4">
        The next material fact will not wait for your workflow.
      </h2>
      <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
        Bring us a live research process. We&apos;ll show you where Kautious
        removes the drag and keeps the evidence.
      </p>
      <div className="flex flex-col sm:flex-row justify-center gap-4">
        <Button
          size="lg"
          variant="secondary"
          className="text-lg px-8 py-4 gap-2"
          asChild
        >
          <a href={CONTACT_URL} target="_blank" rel="noreferrer">
            Request Desktop Access
          </a>
        </Button>
        <Button
          size="lg"
          variant="outline"
          className="text-lg px-8 py-4 bg-transparent text-white border-white hover:bg-white/10"
          asChild
        >
          <Link href="/extract">See Kautious Extract</Link>
        </Button>
      </div>
    </div>
  </section>
);

export default function DesktopPage() {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 w-full z-50 bg-background/95 dark:bg-background/80 backdrop-blur-xl border-b border-border shadow-sm">
        <nav className="max-w-screen-xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Logo />
            <div className="hidden md:block">
              <MainMenu />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="md:hidden">
              <MainMenu />
            </div>
            <Show when="signed-out">
              <SignInButton mode="modal">
                <Button variant="outline" className="shadow-sm">
                  Sign In
                </Button>
              </SignInButton>
            </Show>
            <Show when="signed-in">
              <UserButton />
              <Link href="/console">
                <Button variant="default" className="shadow-sm">
                  Launch App
                </Button>
              </Link>
            </Show>
            <ModeToggle />
          </div>
        </nav>
      </header>

      <main className="relative">
        <DesktopHeroSection />
        <WhatYouGetSection />
        <BuiltForTheDeskSection />
        <HowItWorksSection />
        <PositioningSection />
        <CTASection />
      </main>

      <footer className="border-t bg-background/50 backdrop-blur-sm">
        <div className="max-w-screen-xl mx-auto px-6 py-12">
          <div className="flex flex-col items-center gap-6">
            <Logo />
            <div className="flex items-center gap-6">
              <Link
                href="/terms"
                className="text-sm text-muted-foreground hover:text-primary"
              >
                Terms
              </Link>
              <Link
                href="/privacy"
                className="text-sm text-muted-foreground hover:text-primary"
              >
                Privacy
              </Link>
              <ModeToggle />
            </div>
            <p className="text-sm text-muted-foreground text-center max-w-2xl">
              Kautious provides analytics and research tooling. It is not
              investment advice.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
